import React, { useState } from "react";
import { Box, Paper, Button, IconButton, Typography, TextField, Grid } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import CellPropertyPanel from "./CellPropertyPanel";
import LabelPreview from "./LabelPreview";
import {
  addColumn,
  removeColumn,
  updateColumnWidth,
  addCell,
  removeCell,
  updateCell,
  distribuirEnFilas,
} from "./gridLogic";

function resumenCelda(cell) {
  if (cell.content?.type === "image") {
    return cell.content.src ? "Imagen" : "Imagen (sin elegir)";
  }
  return cell.content?.value ? cell.content.value : "(vacío)";
}

// Editor visual del diseño interno de la etiqueta, con la misma lógica de
// ancho que una grilla web de 12 columnas. El acomodo en pantalla usa
// distribuirEnFilas — la MISMA función que gridToPdfRenderer.js y
// LabelPreview.jsx — así que lo que ves aquí, en la vista previa y en el
// PDF final siempre coinciden.
export default function GridDesigner({
  grid,
  onChange,
  selectedAttributes,
  atributosDisponibles,
  eventoIndex,
  participanteEjemplo,
  eventSubEventsRegistry,
  valueRemap,
  sheetFormat,
}) {
  const [seleccion, setSeleccion] = useState(null); // { columnIndex, cellId }

  const columnaSeleccionada = seleccion && grid.columns[seleccion.columnIndex];
  const celdaSeleccionada =
    columnaSeleccionada && columnaSeleccionada.cells.find((c) => c.id === seleccion.cellId);

  const filasDeColumnas = distribuirEnFilas(grid.columns, (c) => c.width);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5}>
        <LabelPreview
          grid={grid}
          sheetFormat={sheetFormat}
          participante={participanteEjemplo}
          atributosDisponibles={atributosDisponibles}
          eventSubEventsRegistry={eventSubEventsRegistry}
          valueRemap={valueRemap}
        />
      </Grid>

      <Grid item xs={12} md={7}>
        <Typography variant="body2" style={{ opacity: 0.7, marginBottom: 8 }}>
          Cada columna y cada celda mide de 1 a 12 en relación al espacio disponible — igual que en
          una web: si una celda mide 12, la siguiente pasa a la fila de abajo; si dos miden 6 y 6,
          quedan una al lado de la otra.
        </Typography>

        <Box style={{ border: "1px solid #ddd", padding: 8 }}>
          {filasDeColumnas.map((filaColumnas, filaIdx) => {
            const totalWidth = filaColumnas.reduce((acc, c) => acc + (c.width || 12), 0) || 12;
            return (
              <Box key={filaIdx} display="flex" style={{ marginBottom: 8 }}>
                {filaColumnas.map((columna) => {
                  const columnIndex = grid.columns.indexOf(columna);
                  const filasDeCeldas = distribuirEnFilas(columna.cells, (c) => c.width);
                  return (
                    <Box
                      key={columna.id}
                      style={{ width: `${((columna.width || 12) / totalWidth) * 100}%`, padding: 4 }}
                    >
                      <Paper variant="outlined" style={{ padding: 8, height: "100%" }}>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={1}>
                          <TextField
                            label="Ancho columna (1-12)"
                            type="number"
                            size="small"
                            value={columna.width}
                            inputProps={{ min: 1, max: 12 }}
                            onChange={(e) => onChange(updateColumnWidth(grid, columnIndex, e.target.value))}
                            style={{ width: 150 }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              onChange(removeColumn(grid, columnIndex));
                              if (seleccion && seleccion.columnIndex === columnIndex) setSeleccion(null);
                            }}
                            disabled={grid.columns.length <= 1}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        {filasDeCeldas.map((filaCeldas, filaCeldaIdx) => (
                          <Box key={filaCeldaIdx} display="flex" style={{ marginBottom: 4 }}>
                            {filaCeldas.map((cell) => {
                              const totalWidthCelda =
                                filaCeldas.reduce((acc, c) => acc + (c.width || 12), 0) || 12;
                              return (
                                <Box
                                  key={cell.id}
                                  style={{ width: `${((cell.width || 12) / totalWidthCelda) * 100}%`, padding: 2 }}
                                >
                                  <Box
                                    onClick={() => setSeleccion({ columnIndex, cellId: cell.id })}
                                    style={{
                                      cursor: "pointer",
                                      border: "1px solid #ccc",
                                      padding: 6,
                                      minHeight: 40,
                                      backgroundColor:
                                        seleccion && seleccion.cellId === cell.id ? "#e3f2fd" : "#fafafa",
                                    }}
                                  >
                                    <Typography variant="caption" style={{ wordBreak: "break-word" }}>
                                      {resumenCelda(cell)}
                                    </Typography>
                                    <Typography variant="caption" style={{ display: "block", opacity: 0.6 }}>
                                      ancho {cell.width}
                                      {cell.orientation === "vertical" ? " · vertical" : ""}
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        ))}

                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => onChange(addCell(grid, columnIndex))}
                        >
                          Agregar celda
                        </Button>
                      </Paper>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>

        <Button size="small" startIcon={<AddIcon />} onClick={() => onChange(addColumn(grid))} style={{ marginTop: 8 }}>
          Agregar columna
        </Button>

        {celdaSeleccionada && (
          <Paper variant="outlined" style={{ padding: 16, marginTop: 16 }}>
            <Typography variant="subtitle2" gutterBottom>
              Celda seleccionada
            </Typography>
            <CellPropertyPanel
              cell={celdaSeleccionada}
              selectedAttributes={selectedAttributes}
              atributosDisponibles={atributosDisponibles}
              eventoIndex={eventoIndex}
              onChange={(patch) => onChange(updateCell(grid, seleccion.columnIndex, seleccion.cellId, patch))}
            />
            <Box mt={2}>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => {
                  onChange(removeCell(grid, seleccion.columnIndex, seleccion.cellId));
                  setSeleccion(null);
                }}
                disabled={columnaSeleccionada.cells.length <= 1}
              >
                Quitar celda
              </Button>
            </Box>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
}
